import { useQuery } from '@tanstack/react-query';
import { companyProfileService, type CompanyBranding } from '@/services/company-profile.service';
import { useAuthStore } from '@/stores/auth.store';

/** Shown when the tenant has not uploaded branding, or before it loads. */
export const DEFAULT_BRAND_NAME = 'Service Portal';

const EMPTY_BRANDING: CompanyBranding = {
  companyTitle: null,
  logoUrl: null,
  faviconUrl: null,
};

/**
 * Tenant branding for the app shell.
 *
 * Only runs while authenticated: the endpoint is tenant-scoped and there is no
 * tenant context before login, so the pre-auth screens (LoginPage, AuthLayout)
 * cannot use this and keep the product default.
 *
 * Never throws into the shell — on any failure the caller falls back to
 * DEFAULT_BRAND_NAME. A branding lookup must not be able to break the layout.
 */
export function useTenantBranding(): CompanyBranding & { brandName: string } {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const { data } = useQuery({
    queryKey: ['company-profile', 'branding'],
    queryFn: companyProfileService.getBranding,
    enabled: isAuthenticated,
    // Branding changes rarely; avoid refetching it on every window focus.
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const branding = data ?? EMPTY_BRANDING;

  return {
    ...branding,
    brandName: branding.companyTitle?.trim() || DEFAULT_BRAND_NAME,
  };
}
